import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { dossiersAPI, cvAPI } from '../api/api';
import { toast } from '../hooks/use-toast';
import AppLayout from '../components/AppLayout';
import { THEMES, getTheme, DEFAULT_TEMPLATE } from '../lib/dossierThemes';
import { Upload, FileText, Sparkles, CheckCircle2, ArrowRight, RefreshCw, AlertCircle, X, Briefcase, GraduationCap, Wrench, User } from 'lucide-react';

// ───────────────────────────────────────────────────────────────────────────
// NewDossier — streamlined CV-to-editor flow.
//
// State machine:
//   idle        → drop-zone awaits a CV
//   processing  → upload + AI parse running, animated progress bar
//   review      → extracted data shown for confirmation; CTA → /dossiers/:id/edit
//   error       → recoverable; user can retry or start blank
//
// The progress bar is determinate but simulated: we ease it from 0 → 95 % over
// the expected ~60-90 s parse window, then snap to 100 % when the job actually
// completes. This is way more reassuring than an indeterminate spinner for a
// task that takes a long time.
// ───────────────────────────────────────────────────────────────────────────

const PHASE = {
  IDLE: 'idle',
  PROCESSING: 'processing',
  REVIEW: 'review',
  ERROR: 'error',
};

// Eased progress curve — 1 - exp(-t / TAU). Reaches ~63 % at t=TAU,
// ~86 % at 2·TAU, asymptotes near 100 %. We cap at 95 % so the snap to
// 100 % on real completion still feels meaningful.
const PROGRESS_TAU_MS = 30_000;
const PROGRESS_CAP = 0.95;

const easedProgress = (elapsedMs) =>
  Math.min(PROGRESS_CAP, 1 - Math.exp(-elapsedMs / PROGRESS_TAU_MS));

// Normalize parsed CV payload — backend returns either snake_case or camelCase
const normalize = (raw = {}) => ({
  consultant_name: raw.consultant_name || raw.consultantName || '',
  consultant_title: raw.consultant_title || raw.consultantTitle || '',
  competences: raw.competences || '',
  formations: Array.isArray(raw.formations) ? raw.formations : [],
  experiences: Array.isArray(raw.experiences) ? raw.experiences : [],
  languages: Array.isArray(raw.languages) ? raw.languages : [],
});

// Read ?template=<id> from the URL once on mount
const readTemplateFromQuery = () => {
  if (typeof window === 'undefined') return DEFAULT_TEMPLATE;
  try {
    const t = new URLSearchParams(window.location.search).get('template');
    return t && THEMES[t] ? t : DEFAULT_TEMPLATE;
  } catch {
    return DEFAULT_TEMPLATE;
  }
};

// ───────────────────────────────────────────────────────────────────────────
// Main page
// ───────────────────────────────────────────────────────────────────────────
const NewDossier = () => {
  const navigate = useNavigate();

  const [phase, setPhase] = useState(PHASE.IDLE);
  const [statusMsg, setStatusMsg] = useState('');
  const [progress, setProgress] = useState(0);
  const [parsedData, setParsedData] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [creating, setCreating] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [templateId] = useState(readTemplateFromQuery);

  const fileInputRef = useRef(null);
  const startRef = useRef(0);
  const progressTimerRef = useRef(null);
  const cancelledRef = useRef(false);
  const uploadingRef = useRef(false); // guards against concurrent/duplicate uploads

  const theme = getTheme(templateId);

  // ── Cleanup on unmount ──────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      cancelledRef.current = true;
      if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    };
  }, []);

  // ── Drag/drop on entire drop zone ───────────────────────────────────────
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }, []);
  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── File validation + entry into processing pipeline ───────────────────
  const handleFile = async (file) => {
    // Guard against a second upload firing while one is in flight (rapid
    // drop+click, or a retry click) — concurrent uploads each consume a DB
    // connection and can trip the pool on small Supabase tiers.
    if (uploadingRef.current) return;
    if (!file.name.toLowerCase().match(/\.(pdf|doc|docx)$/)) {
      toast({ title: 'Format invalide', description: 'PDF ou Word uniquement (.pdf, .doc, .docx).', variant: 'destructive' });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: 'Fichier trop volumineux', description: 'La taille maximale est de 10 MB.', variant: 'destructive' });
      return;
    }

    uploadingRef.current = true;
    setPhase(PHASE.PROCESSING);
    setProgress(0);
    setErrorMsg('');
    setStatusMsg('Envoi du CV…');
    startRef.current = Date.now();

    // Start the eased progress timer (until we land in REVIEW or ERROR)
    progressTimerRef.current = setInterval(() => {
      const elapsed = Date.now() - startRef.current;
      setProgress(easedProgress(elapsed) * 100);
    }, 200);

    try {
      const uploadRes = await cvAPI.upload(file);
      if (cancelledRef.current) return;

      // Sync path: server already parsed inline (no Redis worker)
      if (!uploadRes.async && uploadRes.result) {
        setStatusMsg("L'IA a terminé l'analyse du CV.");
        await finishProgressAndShowReview(uploadRes.result);
        return;
      }

      // Async path: poll the job until completed/failed
      setStatusMsg("L'IA analyse le CV…");
      const jobId = uploadRes.job_id;

      const MAX_POLLS = 60;
      for (let i = 0; i < MAX_POLLS; i++) {
        await new Promise(r => setTimeout(r, 3000));
        if (cancelledRef.current) return;
        const job = await cvAPI.getJob(jobId);
        if (job.status === 'completed') {
          setStatusMsg("L'IA a terminé l'analyse du CV.");
          await finishProgressAndShowReview(job.result_payload);
          return;
        }
        if (job.status === 'failed') {
          throw new Error(job.error_message || "L'analyse a échoué.");
        }
        // still pending — keep polling, progress bar keeps creeping
      }
      throw new Error("L'analyse a pris trop de temps. Réessayez ou créez un dossier vide.");
    } catch (err) {
      if (cancelledRef.current) return;
      if (progressTimerRef.current) clearInterval(progressTimerRef.current);
      const msg = err?.response?.data?.detail || err.message || "Impossible d'analyser le CV.";
      setErrorMsg(msg);
      setPhase(PHASE.ERROR);
    } finally {
      // Allow a fresh upload once this cycle ends (success, error, or cancel).
      uploadingRef.current = false;
    }
  };

  // ── Snap progress to 100 %, brief checkmark animation, then REVIEW ─────
  const finishProgressAndShowReview = async (raw) => {
    if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    setProgress(100);
    // Hold the success state long enough for the user to see it
    await new Promise(r => setTimeout(r, 900));
    if (cancelledRef.current) return;
    setParsedData(normalize(raw));
    setPhase(PHASE.REVIEW);
  };

  // ── User confirms & we create the dossier, then go to the editor ────────
  const handleConfirm = async () => {
    if (!parsedData || creating) return;
    setCreating(true);
    try {
      const created = await dossiersAPI.create({
        consultant_name: parsedData.consultant_name,
        consultant_title: parsedData.consultant_title,
        name: parsedData.consultant_name || 'Sans titre',
        template_id: templateId,
        competences: parsedData.competences,
        formations: parsedData.formations,
        experiences: parsedData.experiences,
        languages: parsedData.languages,
      });
      toast({ title: 'Dossier créé', description: 'Place à l\'édition !' });
      navigate(`/dossiers/${created.id}/edit`);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Impossible de créer le dossier.';
      toast({ title: 'Erreur', description: msg, variant: 'destructive' });
      setCreating(false);
    }
  };

  // ── Skip path: create empty dossier + jump to editor ────────────────────
  const handleStartBlank = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const created = await dossiersAPI.create({
        consultant_name: '',
        consultant_title: '',
        name: 'Nouveau dossier',
        template_id: templateId,
        competences: '',
        formations: [],
        experiences: [],
      });
      navigate(`/dossiers/${created.id}/edit`);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Impossible de créer le dossier.';
      toast({ title: 'Erreur', description: msg, variant: 'destructive' });
      setCreating(false);
    }
  };

  const handleRetry = () => {
    setPhase(PHASE.IDLE);
    setProgress(0);
    setErrorMsg('');
    setParsedData(null);
  };

  // ── Style helpers ──────────────────────────────────────────────────────
  const cardBase = {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 14,
    padding: 22,
  };

  // Convert competences (string or object) to a flat list of tags for preview
  const competencePreview = (() => {
    const raw = parsedData?.competences;
    if (!raw) return [];
    if (Array.isArray(raw) && raw.length && typeof raw[0] === 'object' && raw[0].domain) {
      return raw.flatMap(g => (g.tags || []).map(t => String(t)));
    }
    if (typeof raw === 'string') {
      return raw.split(/[,;\n]+/).map(s => s.trim()).filter(Boolean);
    }
    if (Array.isArray(raw)) return raw.map(c => String(c));
    return [];
  })();

  return (
    <AppLayout title="Nouveau dossier">
      <div style={{ maxWidth: 720, margin: '20px auto 60px', padding: '0 12px' }}>

        {/* ═══ Hero header — context strip with selected template chip ═══ */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--white)', letterSpacing: -0.3, marginBottom: 4 }}>
              Nouveau dossier de compétences
            </div>
            <div style={{ fontSize: 13, color: 'var(--muted2)' }}>
              Importez un CV — l'IA extrait les informations, vous éditez.
            </div>
          </div>
          <div
            title={`Template : ${theme.label}`}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '6px 10px', borderRadius: 20,
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid var(--border)',
            }}
          >
            <span style={{
              width: 12, height: 12, borderRadius: 3,
              background: theme.coverBg && String(theme.coverBg).startsWith('linear') ? theme.coverBg : theme.pageBorder,
              border: '1px solid rgba(255,255,255,0.12)',
            }} />
            <span style={{ fontSize: 12, color: 'var(--muted2)', fontWeight: 600 }}>{theme.label}</span>
          </div>
        </div>

        {/* ═══ Phase: IDLE — drop zone ═══ */}
        {phase === PHASE.IDLE && (
          <>
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              style={{
                ...cardBase,
                cursor: 'pointer',
                padding: '52px 30px',
                textAlign: 'center',
                borderStyle: 'dashed',
                borderWidth: 2,
                borderColor: dragOver ? 'var(--vio)' : 'rgba(123,104,238,0.25)',
                background: dragOver ? 'rgba(123,104,238,0.06)' : 'var(--surface)',
                transition: 'all 0.18s ease',
              }}
            >
              <div style={{
                width: 72, height: 72,
                margin: '0 auto 18px',
                borderRadius: 18,
                background: 'linear-gradient(135deg, rgba(123,104,238,0.18), rgba(160,153,232,0.12))',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'var(--vio2)',
              }}>
                <Upload size={28} strokeWidth={1.6} />
              </div>
              <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--white)', marginBottom: 6 }}>
                Glissez-déposez un CV ici
              </div>
              <div style={{ fontSize: 13, color: 'var(--muted2)', marginBottom: 18 }}>
                ou cliquez pour parcourir vos fichiers
              </div>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '9px 18px', borderRadius: 8,
                  border: 'none',
                  background: 'var(--vio)', color: '#fff',
                  fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                <FileText size={14} /> Choisir un fichier
              </button>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 14 }}>
                Formats acceptés : PDF, DOC, DOCX · 10 MB max
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleFile(f);
                  e.target.value = '';
                }}
              />
            </div>

            {/* Skip CV path */}
            <div style={{ textAlign: 'center', marginTop: 18 }}>
              <button
                onClick={handleStartBlank}
                disabled={creating}
                style={{
                  background: 'none', border: 'none',
                  color: 'var(--muted2)', fontSize: 12.5, fontWeight: 500,
                  cursor: creating ? 'wait' : 'pointer',
                  fontFamily: 'inherit', textDecoration: 'underline',
                  opacity: creating ? 0.6 : 1,
                }}
              >
                Pas de CV ? Commencer un dossier vide
              </button>
            </div>
          </>
        )}

        {/* ═══ Phase: PROCESSING — animated progress ═══ */}
        {phase === PHASE.PROCESSING && (
          <div style={{ ...cardBase, padding: '40px 32px', textAlign: 'center' }}>
            {/* Pulsing AI brain icon */}
            <div style={{ marginBottom: 24, position: 'relative', height: 88, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div className="nd-pulse-ring" />
              <div className="nd-pulse-ring nd-pulse-ring-2" />
              <div style={{
                position: 'relative', zIndex: 2,
                width: 64, height: 64,
                borderRadius: 16,
                background: 'linear-gradient(135deg, var(--vio), var(--vio2))',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 8px 28px rgba(123,104,238,0.45)',
              }}>
                <Sparkles size={26} color="#fff" strokeWidth={1.8} className="nd-sparkle-rotate" />
              </div>
            </div>

            <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--white)', marginBottom: 6 }}>
              {progress >= 100 ? 'Analyse terminée !' : 'Analyse du CV en cours'}
            </div>
            <div style={{ fontSize: 13, color: 'var(--muted2)', marginBottom: 24, minHeight: 18 }}>
              {statusMsg}
            </div>

            {/* Progress bar */}
            <div style={{
              height: 8,
              background: 'rgba(255,255,255,0.06)',
              borderRadius: 6,
              overflow: 'hidden',
              maxWidth: 420, margin: '0 auto',
            }}>
              <div
                style={{
                  height: '100%',
                  width: `${progress}%`,
                  background: 'linear-gradient(90deg, var(--vio), var(--vio2))',
                  borderRadius: 6,
                  transition: progress >= 100 ? 'width 0.45s ease-out' : 'width 0.4s linear',
                  boxShadow: '0 0 12px rgba(123,104,238,0.5)',
                }}
                className={progress < 100 ? 'nd-bar-shimmer' : ''}
              />
            </div>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 12, fontWeight: 600, letterSpacing: 0.5 }}>
              {Math.round(progress)} %
            </div>

            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 24, lineHeight: 1.6 }}>
              L'analyse prend généralement entre 20 secondes et 2 minutes.
              <br />Vous pouvez laisser cette page ouverte.
            </div>
          </div>
        )}

        {/* ═══ Phase: REVIEW — extracted data + CTA ═══ */}
        {phase === PHASE.REVIEW && parsedData && (
          <>
            {/* Success banner with checkmark animation */}
            <div className="nd-success-banner" style={{
              background: 'linear-gradient(135deg, rgba(34,197,94,0.12), rgba(34,197,94,0.04))',
              border: '1px solid rgba(34,197,94,0.3)',
              borderRadius: 14,
              padding: '18px 22px',
              display: 'flex', alignItems: 'center', gap: 14,
              marginBottom: 18,
            }}>
              <div className="nd-check-pop" style={{
                width: 42, height: 42, borderRadius: '50%',
                background: '#22c55e',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
                boxShadow: '0 0 24px rgba(34,197,94,0.5)',
              }}>
                <CheckCircle2 size={24} color="#fff" strokeWidth={2.2} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--white)', marginBottom: 2 }}>
                  CV analysé avec succès
                </div>
                <div style={{ fontSize: 12, color: 'var(--muted2)' }}>
                  Voici un aperçu des informations extraites par l'IA. Vous pourrez tout corriger dans l'éditeur.
                </div>
              </div>
            </div>

            {/* Extracted data preview cards */}
            <div className="nd-stagger" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 18 }}>
              {/* Identity */}
              <PreviewCard
                icon={<User size={16} />}
                label="Identité"
                style={{ gridColumn: '1 / -1' }}
              >
                <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--white)', marginBottom: 2 }}>
                  {parsedData.consultant_name || <em style={{ color: 'var(--muted)', fontStyle: 'normal' }}>(Nom non détecté)</em>}
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--muted2)' }}>
                  {parsedData.consultant_title || <em style={{ color: 'var(--muted)', fontStyle: 'normal' }}>(Poste non détecté)</em>}
                </div>
              </PreviewCard>

              {/* Experiences */}
              <PreviewCard
                icon={<Briefcase size={16} />}
                label="Expériences"
                count={parsedData.experiences.length}
              >
                {parsedData.experiences.length === 0 ? (
                  <em style={{ fontSize: 12, color: 'var(--muted)' }}>Aucune expérience détectée</em>
                ) : (
                  parsedData.experiences.slice(0, 2).map((e, i) => (
                    <div key={i} style={{ fontSize: 12, color: 'var(--muted2)', marginBottom: 4, lineHeight: 1.4 }}>
                      <div style={{ color: 'var(--white)', fontWeight: 600 }}>{e.poste || '—'}</div>
                      <div style={{ fontSize: 11 }}>{[e.entreprise, e.duree].filter(Boolean).join(' · ')}</div>
                    </div>
                  ))
                )}
                {parsedData.experiences.length > 2 && (
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
                    + {parsedData.experiences.length - 2} autre{parsedData.experiences.length - 2 !== 1 ? 's' : ''}
                  </div>
                )}
              </PreviewCard>

              {/* Formations */}
              <PreviewCard
                icon={<GraduationCap size={16} />}
                label="Formations"
                count={parsedData.formations.length}
              >
                {parsedData.formations.length === 0 ? (
                  <em style={{ fontSize: 12, color: 'var(--muted)' }}>Aucune formation détectée</em>
                ) : (
                  parsedData.formations.slice(0, 2).map((f, i) => (
                    <div key={i} style={{ fontSize: 12, color: 'var(--muted2)', marginBottom: 4, lineHeight: 1.4 }}>
                      <div style={{ color: 'var(--white)', fontWeight: 600 }}>{f.title || '—'}</div>
                      <div style={{ fontSize: 11 }}>{[f.institution, f.year].filter(Boolean).join(' · ')}</div>
                    </div>
                  ))
                )}
                {parsedData.formations.length > 2 && (
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
                    + {parsedData.formations.length - 2} autre{parsedData.formations.length - 2 !== 1 ? 's' : ''}
                  </div>
                )}
              </PreviewCard>

              {/* Competences */}
              <PreviewCard
                icon={<Wrench size={16} />}
                label="Compétences"
                count={competencePreview.length}
                style={{ gridColumn: '1 / -1' }}
              >
                {competencePreview.length === 0 ? (
                  <em style={{ fontSize: 12, color: 'var(--muted)' }}>Aucune compétence détectée</em>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                    {competencePreview.slice(0, 12).map((c, i) => (
                      <span key={i} className="sd-chip">{c}</span>
                    ))}
                    {competencePreview.length > 12 && (
                      <span className="sd-chip" style={{ opacity: 0.6 }}>+{competencePreview.length - 12}</span>
                    )}
                  </div>
                )}
              </PreviewCard>
            </div>

            {/* CTA row */}
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={handleConfirm}
                disabled={creating}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '11px 22px', borderRadius: 9,
                  border: 'none',
                  background: 'linear-gradient(135deg, var(--vio), var(--vio2))',
                  color: '#fff',
                  fontSize: 14, fontWeight: 700,
                  cursor: creating ? 'wait' : 'pointer',
                  fontFamily: 'inherit',
                  boxShadow: '0 6px 20px rgba(123,104,238,0.35)',
                  opacity: creating ? 0.7 : 1,
                  transition: 'all 0.15s',
                }}
              >
                {creating ? (
                  <RefreshCw size={15} className="nd-spin" />
                ) : (
                  <ArrowRight size={15} />
                )}
                {creating ? 'Création…' : "Continuer vers l'éditeur"}
              </button>
              <button
                onClick={handleRetry}
                disabled={creating}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '10px 16px', borderRadius: 8,
                  border: '1px solid var(--border)',
                  background: 'transparent',
                  color: 'var(--muted2)',
                  fontSize: 13, fontWeight: 500,
                  cursor: creating ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                <X size={13} /> Recharger un autre CV
              </button>
            </div>
          </>
        )}

        {/* ═══ Phase: ERROR ═══ */}
        {phase === PHASE.ERROR && (
          <div style={{
            ...cardBase,
            background: 'rgba(239,68,68,0.06)',
            border: '1px solid rgba(239,68,68,0.3)',
            padding: '28px 26px',
            textAlign: 'center',
          }}>
            <div style={{
              width: 52, height: 52, borderRadius: 14,
              background: 'rgba(239,68,68,0.15)',
              color: '#F87171',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 14px',
            }}>
              <AlertCircle size={24} strokeWidth={1.8} />
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--white)', marginBottom: 6 }}>
              L'analyse a échoué
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--muted2)', marginBottom: 18, lineHeight: 1.5 }}>
              {errorMsg}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={handleRetry}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '9px 18px', borderRadius: 8,
                  border: 'none', background: 'var(--vio)', color: '#fff',
                  fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                <RefreshCw size={13} /> Réessayer
              </button>
              <button
                onClick={handleStartBlank}
                disabled={creating}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '9px 18px', borderRadius: 8,
                  border: '1px solid var(--border)',
                  background: 'transparent', color: 'var(--muted2)',
                  fontSize: 13, fontWeight: 500, cursor: creating ? 'wait' : 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Commencer sans CV
              </button>
            </div>
          </div>
        )}

      </div>

      {/* ═══ Animations ═══ */}
      <style>{`
        @keyframes nd-pulse {
          0%   { transform: scale(0.6); opacity: 0.55; }
          80%  { transform: scale(1.6); opacity: 0; }
          100% { transform: scale(1.6); opacity: 0; }
        }
        .nd-pulse-ring {
          position: absolute;
          width: 64px; height: 64px;
          border-radius: 50%;
          background: rgba(123,104,238,0.25);
          animation: nd-pulse 1.8s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        .nd-pulse-ring-2 { animation-delay: 0.6s; }

        @keyframes nd-sparkle-rotate {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        .nd-sparkle-rotate { animation: nd-sparkle-rotate 4s linear infinite; }

        @keyframes nd-shimmer-bg {
          0%   { background-position: -100% 0; }
          100% { background-position: 200% 0; }
        }
        .nd-bar-shimmer {
          background-image: linear-gradient(90deg, var(--vio), var(--vio2)),
                            linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.35) 50%, transparent 100%);
          background-blend-mode: normal, overlay;
          background-size: 100% 100%, 80px 100%;
          animation: nd-shimmer-bg 1.4s linear infinite;
        }

        @keyframes nd-check-pop {
          0%   { transform: scale(0); opacity: 0; }
          55%  { transform: scale(1.2); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        .nd-check-pop { animation: nd-check-pop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) backwards; }

        @keyframes nd-success-fade {
          from { opacity: 0; transform: translateY(-8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .nd-success-banner { animation: nd-success-fade 0.4s ease-out backwards; }

        @keyframes nd-stagger-in {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .nd-stagger > * { animation: nd-stagger-in 0.45s ease-out backwards; }
        .nd-stagger > *:nth-child(1) { animation-delay: 0.10s; }
        .nd-stagger > *:nth-child(2) { animation-delay: 0.18s; }
        .nd-stagger > *:nth-child(3) { animation-delay: 0.26s; }
        .nd-stagger > *:nth-child(4) { animation-delay: 0.34s; }

        @keyframes nd-spin { to { transform: rotate(360deg); } }
        .nd-spin { animation: nd-spin 0.9s linear infinite; }
      `}</style>
    </AppLayout>
  );
};

// ───────────────────────────────────────────────────────────────────────────
// PreviewCard — small data card used in the REVIEW phase
// ───────────────────────────────────────────────────────────────────────────
const PreviewCard = ({ icon, label, count, children, style = {} }) => (
  <div style={{
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 12,
    padding: '14px 16px',
    minWidth: 0,
    ...style,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        <span style={{
          width: 26, height: 26, borderRadius: 7,
          background: 'rgba(123,104,238,0.12)',
          color: 'var(--vio2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {icon}
        </span>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--muted2)' }}>
          {label}
        </span>
      </div>
      {typeof count === 'number' && (
        <span style={{
          fontSize: 11, fontWeight: 700,
          padding: '1px 7px', borderRadius: 10,
          background: 'rgba(123,104,238,0.15)',
          color: 'var(--vio2)',
        }}>{count}</span>
      )}
    </div>
    <div>{children}</div>
  </div>
);

export default NewDossier;
